import { inject, injectable } from 'inversify'
import type { PaginationUrlFilterScalarValueType } from '@/services/tables/types'
import {
  type FilterToFormattedTextConvertable,
  type FormattedTextNode,
  type Operation,
  PhoneFilterOperation,
} from '@/services/types'
import { SERVICE_TYPES } from '@/core/container/types'
import type TranslateService from '@/services/translateService'
import TmLogicError from '@/core/error/tmLogicError'
import { coersceBoolean } from '@/utils/boolean/coersceBoolean'
import { getFullFilledOperations } from '@/services/filters/getFullFilledOperations'

interface ParsedValue {
  value: string[]
  hasValue: boolean
}

const divider = ', '

@injectable()
export default class PhoneFilterMapper implements FilterToFormattedTextConvertable {
  constructor(@inject(SERVICE_TYPES.TranslateService) protected readonly translateService: TranslateService) {}

  public mapOperation(operation: Operation, value: unknown) {
    switch (operation) {
      case 'startsWith':
        return PhoneFilterOperation.StartsWith
      case 'endsWith':
        return PhoneFilterOperation.EndsWith
      case 'anyOf':
        return PhoneFilterOperation.AnyOf
      case 'noneOf':
        return PhoneFilterOperation.NoneOf
      case 'isEmpty':
        return coersceBoolean(value, PhoneFilterOperation.IsEmpty, PhoneFilterOperation.IsNotEmpty)
      default:
        throw new TmLogicError('Unknown operation')
    }
  }

  public parseValue(value: unknown, phoneFilterOperation: PhoneFilterOperation): ParsedValue {
    if (
      phoneFilterOperation === PhoneFilterOperation.IsEmpty ||
      phoneFilterOperation === PhoneFilterOperation.IsNotEmpty
    ) {
      return {
        value: [],
        hasValue: true,
      }
    }
    if (typeof value === 'string') {
      return {
        value: [value],
        hasValue: true,
      }
    }
    if (Array.isArray(value)) {
      return {
        value,
        hasValue: value.length > 0,
      }
    }

    return {
      value: [],
      hasValue: false,
    }
  }

  public getLabel(phoneFilterOperation: PhoneFilterOperation, parsedValue: ParsedValue, maxItemsCount?: number) {
    if (phoneFilterOperation === PhoneFilterOperation.IsEmpty) {
      return {
        op: this.translateService.t('tmFilterPhone.options.isEmpty'),
        main: '',
      }
    }
    if (phoneFilterOperation === PhoneFilterOperation.IsNotEmpty) {
      return {
        op: this.translateService.t('tmFilterPhone.options.isNotEmpty'),
        main: '',
      }
    }

    if (!parsedValue.hasValue) {
      return {
        op: '',
        main: this.translateService.t('common.any'),
      }
    }

    let prefix = ''
    if (phoneFilterOperation === PhoneFilterOperation.StartsWith) {
      prefix = this.translateService.t('tmFilterPhone.options.startsWith')
    } else if (phoneFilterOperation === PhoneFilterOperation.EndsWith) {
      prefix = this.translateService.t('tmFilterPhone.options.endsWith')
    } else if (phoneFilterOperation === PhoneFilterOperation.NoneOf) {
      prefix = this.translateService.t('tmFilterPhone.options.notLike')
    } else if (phoneFilterOperation === PhoneFilterOperation.AnyOf) {
      prefix = this.translateService.t('tmFilterPhone.options.like')
    }

    return {
      op: prefix,
      main: maxItemsCount ? parsedValue.value.slice(0, maxItemsCount).join(divider) : parsedValue.value.join(divider),
    }
  }

  public toFormattedText(filterValue: PaginationUrlFilterScalarValueType): FormattedTextNode[] {
    const operation = getFullFilledOperations(filterValue)[0]
    const value = filterValue[operation]
    const phoneOperation = this.mapOperation(operation, value)
    const parsedValue = this.parseValue(value, phoneOperation)
    const label = this.getLabel(phoneOperation, parsedValue)
    const result: FormattedTextNode[] = []
    if (label.op) {
      result.push({ type: 'italic', value: label.op })
    }
    if (label.main) {
      result.push({
        type: 'badges',
        value: label.main.split(divider).map((part) => part),
      })
    }
    return result
  }
}
